import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, deleteClient } from '../services/clientService';
import { SOCKET_URL } from '../config/env';
import Drawer from '../components/Drawer';
import ClientForm from '../components/ClientForm';
import { io } from 'socket.io-client';
import {
    Plus, Search, ArrowRight, Trash2, Loader2, Flame,
    ChevronLeft, ChevronRight, Users, Smartphone
} from 'lucide-react';
import { motion } from 'framer-motion';

const Clients = () => {
    const navigate = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingHeader, setEditingHeader] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const [filters, setFilters] = useState({ name: '', cin: '', type: '', status: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;

    const socketRef = useRef(null);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const filteredTenants = tenants.filter(t => {
        const matchName = !filters.name || (t.company_name || t.name || '').toLowerCase().includes(filters.name.toLowerCase()) || (t.hostName || '').toLowerCase().includes(filters.name.toLowerCase());
        const matchCin = !filters.cin || (t.cin || '00000000').toLowerCase().includes(filters.cin.toLowerCase());
        const matchType = !filters.type || (t.Espace || 'Partagé').toLowerCase().includes(filters.type.toLowerCase());
        const matchStatus = !filters.status || (t.status === 'running' ? 'actif' : 'inactif').includes(filters.status.toLowerCase());
        return matchName && matchCin && matchType && matchStatus;
    });

    const totalPages = Math.max(1, Math.ceil(filteredTenants.length / itemsPerPage));
    const currentTenants = filteredTenants.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const updateFromData = (data) => {
        setTenants(data || []);
        setLoading(false);
    };

    const fetchData = async () => {
        try {
            const data = await getClients();
            updateFromData(data);
        } catch (err) {
            console.error('Error fetching clients:', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const socket = io(SOCKET_URL, { withCredentials: true });
        socketRef.current = socket;
        socket.on('clients:update', updateFromData);
        return () => { socket.disconnect(); socketRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        setDeletingId(id);
        setDeleteConfirmId(null);
        try {
            await deleteClient(id);
            fetchData();
        } catch (err) {
            console.error('Failed to delete client:', err);
            alert('Erreur lors de la suppression du domaine.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="flex-1 bg-white flex flex-col relative min-h-screen">

            {/* ── Sticky Toolbar Header ── */}
            <div className="sticky top-0 z-30 bg-slate-50 border-b border-slate-200 px-8 py-3 flex items-center justify-between shadow-xs">
                <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-600 shadow-xs">
                        <Users size={16} />
                    </div>
                    <div>
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Table de Routage Active</span>
                        <span className="ml-3 text-[10px] font-bold text-slate-400 bg-slate-200/60 px-2 py-0.5 rounded-md font-mono">{tenants.length} Enregistrements</span>
                    </div>
                </div>
                <div className="flex items-center gap-2.5">
                    <button
                        onClick={() => navigate('/app-mobile')}
                        className="inline-flex items-center gap-2 px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 hover:border-emerald-300 rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                        title="Gérer l'application mobile globale"
                    >
                        <Smartphone size={14} className="text-emerald-600" />
                        <span>App Mobile Globale</span>
                    </button>
                    <button
                        onClick={() => setIsCreateOpen(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-all shadow-sm"
                    >
                        <Plus size={14} />
                        Nouveau Client
                    </button>
                </div>
            </div>

            {/* ── Table ── */}
            <div className="flex-1 overflow-visible">
                <table className="w-full text-left border-collapse mb-12">
                    <thead>
                        <tr>
                            {/* Col: Nom Organisation (filterable) */}
                            <th
                                className="px-8 py-4 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest w-1/4 cursor-pointer group select-none"
                                onDoubleClick={() => setEditingHeader('name')}
                            >
                                {editingHeader === 'name' || filters.name ? (
                                    <div className="flex items-center gap-2">
                                        <Search size={12} className="text-blue-500 shrink-0" />
                                        <input
                                            autoFocus={editingHeader === 'name'}
                                            type="text"
                                            value={filters.name}
                                            onChange={e => handleFilterChange('name', e.target.value)}
                                            onBlur={() => setEditingHeader(null)}
                                            className="bg-transparent border-b border-blue-500 outline-none text-slate-900 placeholder:text-slate-400 font-bold w-full text-xs"
                                            placeholder="Rechercher..."
                                        />
                                        {filters.name && (
                                            <button onClick={() => handleFilterChange('name', '')} className="text-slate-400 hover:text-slate-700 shrink-0 text-lg leading-none">&times;</button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2" title="Double-cliquez pour rechercher">
                                        <span>Nom de l'Organisation</span>
                                        <Search size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                )}
                            </th>

                            {/* Col: Identifiant (filterable) */}
                            <th
                                className="px-8 py-4 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest w-1/4 cursor-pointer group select-none"
                                onDoubleClick={() => setEditingHeader('cin')}
                            >
                                {editingHeader === 'cin' || filters.cin ? (
                                    <div className="flex items-center gap-2">
                                        <Search size={12} className="text-blue-500 shrink-0" />
                                        <input
                                            autoFocus={editingHeader === 'cin'}
                                            type="text"
                                            value={filters.cin}
                                            onChange={e => handleFilterChange('cin', e.target.value)}
                                            onBlur={() => setEditingHeader(null)}
                                            className="bg-transparent border-b border-blue-500 outline-none text-slate-900 placeholder:text-slate-400 font-bold w-full text-xs"
                                            placeholder="Rechercher..."
                                        />
                                        {filters.cin && (
                                            <button onClick={() => handleFilterChange('cin', '')} className="text-slate-400 hover:text-slate-700 shrink-0 text-lg leading-none">&times;</button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2" title="Double-cliquez pour rechercher">
                                        <span>Identifiant Officiel</span>
                                        <Search size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                )}
                            </th>

                            {/* Col: Type de Structure (filterable) */}
                            <th
                                className="px-8 py-4 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest w-1/6 cursor-pointer group select-none"
                                onDoubleClick={() => setEditingHeader('type')}
                            >
                                {editingHeader === 'type' || filters.type ? (
                                    <div className="flex items-center gap-2">
                                        <Search size={12} className="text-blue-500 shrink-0" />
                                        <input
                                            autoFocus={editingHeader === 'type'}
                                            type="text"
                                            value={filters.type}
                                            onChange={e => handleFilterChange('type', e.target.value)}
                                            onBlur={() => setEditingHeader(null)}
                                            className="bg-transparent border-b border-blue-500 outline-none text-slate-900 placeholder:text-slate-400 font-bold w-full text-xs"
                                            placeholder="Filtrer..."
                                        />
                                        {filters.type && (
                                            <button onClick={() => handleFilterChange('type', '')} className="text-slate-400 hover:text-slate-700 shrink-0 text-lg leading-none">&times;</button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2" title="Double-cliquez pour filtrer">
                                        <span>Type de Structure</span>
                                        <Search size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                )}
                            </th>

                            {/* Col: Statut (filterable) */}
                            <th
                                className="px-8 py-4 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest w-1/6 cursor-pointer group select-none"
                                onDoubleClick={() => setEditingHeader('status')}
                            >
                                {editingHeader === 'status' || filters.status ? (
                                    <div className="flex items-center gap-2">
                                        <Search size={12} className="text-blue-500 shrink-0" />
                                        <input
                                            autoFocus={editingHeader === 'status'}
                                            type="text"
                                            value={filters.status}
                                            onChange={e => handleFilterChange('status', e.target.value)}
                                            onBlur={() => setEditingHeader(null)}
                                            className="bg-transparent border-b border-blue-500 outline-none text-slate-900 placeholder:text-slate-400 font-bold w-full text-xs"
                                            placeholder="actif / inactif..."
                                        />
                                        {filters.status && (
                                            <button onClick={() => handleFilterChange('status', '')} className="text-slate-400 hover:text-slate-700 shrink-0 text-lg leading-none">&times;</button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2" title="Double-cliquez pour filtrer">
                                        <span>Statut</span>
                                        <Search size={10} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                )}
                            </th>

                            <th className="px-8 py-4 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">
                                Action
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {currentTenants.map(tenant => {
                            const id = tenant.id || tenant._id;
                            const isDeleting = deletingId === id;
                            const isActive = tenant.status?.toLowerCase() === 'running';

                            return (
                                <motion.tr
                                    key={id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className={`group border-b border-slate-100 transition-colors relative ${isDeleting ? 'bg-slate-50/50 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'} ${deleteConfirmId === id ? 'z-20' : 'z-0'}`}
                                    onClick={() => !isDeleting && navigate(`/control/tenant/${id}`)}
                                >
                                    {isDeleting && (
                                        <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center border-b border-slate-100">
                                            <div className="flex items-center gap-2 text-slate-500 font-bold text-xs bg-white px-4 py-2 rounded-full shadow-sm border border-slate-200">
                                                <Loader2 size={14} className="animate-spin text-red-500" />
                                                Suppression en cours...
                                            </div>
                                        </div>
                                    )}

                                    {/* Nom de l'Organisation */}
                                    <td className="px-8 py-4">
                                        <div className="text-sm font-bold text-slate-900 tracking-tight">{tenant.company_name || tenant.name}</div>
                                        <div className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">{tenant.hostName || '—'}</div>
                                    </td>

                                    {/* CIN */}
                                    <td className="px-8 py-4">
                                        <div className="text-sm font-medium text-slate-600 font-mono">{tenant.cin || '00000000'}</div>
                                        <div className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">CIN</div>
                                    </td>

                                    {/* Type */}
                                    <td className="px-8 py-4">
                                        <div className="text-xs font-bold text-slate-600 tracking-tight uppercase">{tenant.Espace || 'Partagé'}</div>
                                        <div className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">Environnement</div>
                                    </td>

                                    {/* Statut */}
                                    <td className="px-8 py-4">
                                        {isActive ? (
                                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-widest border border-emerald-200/60 rounded">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                Actif
                                            </div>
                                        ) : (
                                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest border border-slate-200 rounded">
                                                <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                                Inactif
                                            </div>
                                        )}
                                    </td>

                                    {/* Actions */}
                                    <td className="px-8 py-4 text-right" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-2 relative">
                                            {/* Delete confirm popover */}
                                            {deleteConfirmId === id && (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95, x: 10 }}
                                                    animate={{ opacity: 1, scale: 1, x: 0 }}
                                                    className="absolute right-16 bottom-0 z-[9999] bg-white border border-red-100 shadow-[0_0_40px_-10px_rgba(0,0,0,0.25)] rounded-xl p-4 flex flex-col gap-2 w-72 origin-bottom-right"
                                                >
                                                    <div className="flex items-center gap-2 text-red-500 font-black text-xs">
                                                        <Flame size={14} />
                                                        Suppression définitive
                                                    </div>
                                                    <p className="text-[10px] text-slate-500 leading-relaxed">
                                                        Êtes-vous sûr de vouloir supprimer ce domaine et <b>tous ses conteneurs</b> ? Cette action est irréversible.
                                                    </p>
                                                    <div className="flex justify-end gap-2 mt-1">
                                                        <button
                                                            onClick={() => setDeleteConfirmId(null)}
                                                            className="px-3 py-1.5 text-[10px] font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                                                        >
                                                            Annuler
                                                        </button>
                                                        <button
                                                            onClick={e => handleDelete(e, id)}
                                                            className="px-3 py-1.5 text-[10px] font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-1.5"
                                                        >
                                                            <Trash2 size={11} />
                                                            Confirmer
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}

                                            {/* Delete btn */}
                                            <button
                                                onClick={() => setDeleteConfirmId(deleteConfirmId === id ? null : id)}
                                                disabled={isDeleting}
                                                title="Supprimer"
                                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all ${deleteConfirmId === id
                                                    ? 'bg-red-50 border-red-200 text-red-500'
                                                    : 'border-red-200 text-red-400 hover:border-red-600 hover:bg-red-600 hover:text-white group-hover:scale-110'
                                                    } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>

                                            {/* Navigate btn */}
                                            <button
                                                onClick={() => navigate(`/control/tenant/${id}`)}
                                                disabled={isDeleting}
                                                title="Gérer"
                                                className={`inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 text-slate-400 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-all group-hover:scale-110 ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <ArrowRight size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            );
                        })}

                        {!loading && filteredTenants.length === 0 && (
                            <tr>
                                <td colSpan="5" className="px-8 py-16 text-center text-sm font-medium text-slate-400">
                                    Aucun domaine correspondant à votre recherche.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Pagination ── */}
            <div className="border-t border-slate-200 bg-white px-8 py-4 flex items-center justify-between shrink-0 sticky bottom-0">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Affichage {filteredTenants.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredTenants.length)} sur {filteredTenants.length}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <div className="text-xs font-bold text-slate-700 px-2 font-mono">
                        {currentPage} / {totalPages}
                    </div>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>

            {/* ── Create Drawer ── */}
            <Drawer isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Déploiement Nouvelle Instance">
                <ClientForm
                    onSuccess={() => { setIsCreateOpen(false); fetchData(); }}
                    onCancel={() => setIsCreateOpen(false)}
                />
            </Drawer>
        </div>
    );
};

export default Clients;
